import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bitrixPartnerLookup } from "../../redux/profile/authAndReg/bitrixPartnerLookup";
import { bitrixRegister } from "../../redux/profile/authAndReg/bitrixRegister";
import { bitrixRegisterChild } from "../../redux/profile/authAndReg/bitrixRegisterChild";
import { setPartnerId } from "../../redux/user/slice";
import { axiosInstance } from "../../utils/axios/requestInfo";
import { api } from "../../resources/api";
import { RootState } from "../../redux/store";
import { formatDateToISOWithTime } from "../../helpers/dateFormatter";
import { fetchAddProfile } from "../../redux/profile/profileInfo/asyncActions";

export const useLookUpUsers = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const parentPartnerId = useSelector((state: RootState) => state.user.partnerId);

  const getBitrixUser = useCallback(
    async (
      phone: string | null = null,
      partnerId: string | null = null,
      birthDate: string | null = null,
      profileName: string | null = null,
      skipPartnerIdCheck: boolean = false
    ): Promise<string | number | null> => {
      try {
        const bitrixUser = await bitrixPartnerLookup({
          phone: phone || undefined,
          partnerId: partnerId || undefined,
          birthday: birthDate || undefined,
          profileName: profileName || undefined,
          skipPartnerIdCheck,
        });

        return bitrixUser;
      } catch (error) {
        return null;
      }
    },
    []
  );

  const registerBitrixUser = useCallback(
    async (
      typeProfile: "adult" | "child",
      phone: string | null = null,
      email: string | null = null,
      profileName: string | null = null,
      partnerId: string | null = null,
      birthDate: string | null = null,
      skipPartnerIdCheck: boolean = false
    ): Promise<string | number | null> => {
      try {
        let bitrixPartnerId: string | number | null = null;

        if (typeProfile === "adult") {
          bitrixPartnerId = await getBitrixUser(
            phone,
            null,
            null,
            null,
            skipPartnerIdCheck
          );
        } else {
          bitrixPartnerId = await getBitrixUser(
            null,
            partnerId,
            birthDate,
            profileName,
            skipPartnerIdCheck
          );
        }

        if (bitrixPartnerId) {
          return bitrixPartnerId;
        }

        if (typeProfile === "adult") {
          bitrixPartnerId = await bitrixRegister(
            phone!,
            profileName!,
            email!,
            skipPartnerIdCheck
          );
        } else {
          bitrixPartnerId = await bitrixRegisterChild(
            partnerId!,
            profileName!,
            birthDate!,
            skipPartnerIdCheck
          );
        }

        return bitrixPartnerId;
      } catch (error) {
        throw error;
      }
    },
    [getBitrixUser]
  );

  const updateUser = useCallback(
    async (data: {
      name?: string;
      phone?: string;
      email?: string;
      partner_id?: string | number;
    }) => {
      try {
        await axiosInstance.put(api.profile.settings.getProfile.GET_PROFILE, data);
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const createBackProfile = useCallback(
    async (
      profileType: "adult" | "child",
      profile: { name: string; birthDate?: string },
      bitrixUser: string | number,
      phoneTemp: string
    ) => {
      const formattedBirthDate =
        profileType === "child" && profile.birthDate
          ? formatDateToISOWithTime(profile.birthDate)
          : null;

      try {
        const createProfilePayload = {
          name: profile.name,
          is_adult: profileType === "adult",
          birth_date: formattedBirthDate,
          phone: profileType === "adult" ? phoneTemp : "",
          partner_id: bitrixUser,
        };

        await dispatch(fetchAddProfile(createProfilePayload) as any);
      } catch (error: any) {
        const status = error?.response?.status;
        const data = error?.response?.data;
        const message =
          typeof data === "string" ? data : data?.message || data?.error || "";
        const lowered = (message || "").toString().toLowerCase();
        const alreadyExists =
          status === 409 ||
          lowered.includes("already exist") ||
          lowered.includes("already_exists") ||
          lowered.includes("уже существует") ||
          lowered.includes("уже создан");

        if (!alreadyExists) {
          throw error;
        }
      }
    },
    [dispatch]
  );

  const signUpUser = useCallback(
    async (
      authData: { name: string; email: string },
      phoneTemp: string,
      code?: string
    ) => {
      try {
        const partnerId = await registerBitrixUser(
          "adult",
          phoneTemp,
          authData.email,
          authData.name,
          null,
          null,
          true
        );

        if (partnerId) {
          dispatch(setPartnerId(partnerId));

          await updateUser({
            name: authData.name,
            phone: phoneTemp,
            email: authData.email,
            partner_id: partnerId,
          });
        }
      } catch (error) {
        throw error;
      }
    },
    [registerBitrixUser, updateUser, dispatch]
  );

  const handleSaveProfile = useCallback(
    async (profile: { name: string; phone?: string; birthDate?: string }) => {
      const profileType = !profile.birthDate ? "adult" : "child";

      const phoneTemp = profile.phone ? `+${profile.phone.replace(/\D/g, "")}` : "";

      const parentId = profileType === "child" ? parentPartnerId?.toString() || "" : "";

      const formattedBirthDate =
        profileType === "child" && profile.birthDate
          ? formatDateToISOWithTime(profile.birthDate)
          : null;

      let bitrixUser = await registerBitrixUser(
        profileType,
        phoneTemp,
        user?.email || "",
        profile.name,
        parentId,
        formattedBirthDate,
        false
      );

      if (bitrixUser) {
        await createBackProfile(profileType, profile, bitrixUser, phoneTemp);
      }

      return bitrixUser;
    },
    [registerBitrixUser, createBackProfile, parentPartnerId, user]
  );

  const authorizationPartnerIdCheck = useCallback(
    async (phone: string) => {
      try {
        const bitrixUser = await getBitrixUser(phone, null, null, null, true);

        if (bitrixUser) {
          dispatch(setPartnerId(bitrixUser));

          await updateUser({
            partner_id: bitrixUser,
          });
        }
      } catch (error) {
      }
    },
    [getBitrixUser, updateUser, dispatch]
  );

  return {
    getBitrixUser,
    registerBitrixUser,
    signUpUser,
    updateUser,
    authorizationPartnerIdCheck,
    handleSaveProfile,
  };
};
